import type { Command, SearchItem, UsageMap, ParsedInput, SearchKind } from './types';

// ─── Input parsing ─────────────────────────────────────────────

const SCOPE_ALIASES: Record<string, SearchKind> = {
  table: 'table',
  tables: 'table',
  view: 'view',
  views: 'view',
  col: 'column',
  column: 'column',
  columns: 'column',
  query: 'saved-query',
  queries: 'saved-query',
  saved: 'saved-query',
  tab: 'tab',
  tabs: 'tab',
  recent: 'recent-tab',
  conn: 'connection',
  connection: 'connection',
  connections: 'connection',
  tunnel: 'tunnel',
  tunnels: 'tunnel',
  db: 'database',
  database: 'database',
  databases: 'database',
  schema: 'schema',
  schemas: 'schema',
  fn: 'function',
  func: 'function',
  function: 'function',
  functions: 'function',
  file: 'file',
  files: 'file',
  history: 'history',
};

/**
 * Parse the raw bar input into mode + scope + query. Command mode is marked
 * by a leading `>`; scope filters are `@<word>` at position 0 of whatever
 * remains. Both prefixes are consumed from the returned `query`.
 */
export function parseInput(raw: string): ParsedInput {
  let rest = raw;
  let mode: ParsedInput['mode'] = 'search';

  if (rest.startsWith('>')) {
    mode = 'command';
    rest = rest.slice(1).trimStart();
    return { mode, scope: null, query: rest };
  }

  if (rest.startsWith('@')) {
    const match = rest.match(/^@(\w+)\s*/);
    if (match) {
      const scope = SCOPE_ALIASES[match[1].toLowerCase()] ?? null;
      if (scope) {
        return { mode, scope, query: rest.slice(match[0].length) };
      }
    }
  }

  return { mode, scope: null, query: rest };
}

// ─── Fuzzy match ───────────────────────────────────────────────

/**
 * Lightweight fuzzy scorer. Returns 0 if no match, else a number in (0, 1].
 * Higher is better. Weighted across three fields so label matches beat
 * subtitle matches which beat keyword matches.
 *
 * Strategy: literal substring match first (fast, high quality). If that
 * misses, fall back to subsequence match with a gap penalty.
 */
export function fuzzyScore(
  query: string,
  label: string,
  subtitle: string | undefined,
  keywords: string[] | undefined,
): number {
  if (!query) return 1;
  const q = query.toLowerCase();

  const labelScore = fieldScore(q, label.toLowerCase()) * 1.0;
  const subScore = subtitle ? fieldScore(q, subtitle.toLowerCase()) * 0.5 : 0;
  const kwScore = keywords?.length
    ? Math.max(...keywords.map((k) => fieldScore(q, k.toLowerCase()))) * 0.7
    : 0;

  return Math.max(labelScore, subScore, kwScore);
}

function fieldScore(q: string, field: string): number {
  if (field === q) return 1;
  const idx = field.indexOf(q);
  if (idx === 0) return 0.9;          // prefix
  if (idx > 0) return 0.75 - Math.min(0.25, idx / field.length * 0.25); // substring, slight penalty for late match
  // Subsequence fallback
  let fi = 0;
  let matches = 0;
  let gap = 0;
  let totalGap = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const c = q[qi];
    while (fi < field.length && field[fi] !== c) {
      fi++;
      gap++;
    }
    if (fi === field.length) return 0;
    matches++;
    totalGap += gap;
    gap = 0;
    fi++;
  }
  if (matches < q.length) return 0;
  const density = q.length / (q.length + totalGap);
  return 0.3 * density;               // max 0.3 for pure subsequence
}

// ─── Recency + frequency blend ────────────────────────────────

const RECENCY_HALF_LIFE_DAYS = 7;
const DECAY_CUTOFF_DAYS = 60;

/** Blended usage score in [0, 1]. Zero when the item has never been used. */
export function usageScore(
  id: string,
  usage: UsageMap,
  maxCount: number,
  now: number = Date.now(),
): number {
  const rec = usage[id];
  if (!rec) return 0;
  const daysSince = (now - rec.lastUsedAt) / 86_400_000;
  if (daysSince > DECAY_CUTOFF_DAYS) return 0;
  const recency = Math.exp(-daysSince / RECENCY_HALF_LIFE_DAYS);
  const frequency = maxCount > 0 ? Math.log1p(rec.count) / Math.log1p(maxCount) : 0;
  return 0.6 * recency + 0.4 * frequency;
}

/** Max useCount in the map — denominator for frequency normalization. */
export function maxUseCount(usage: UsageMap): number {
  let m = 0;
  for (const id in usage) if (usage[id].count > m) m = usage[id].count;
  return m;
}

// ─── Ranking ──────────────────────────────────────────────────

/**
 * Combine a fuzzy match score with an item's usage score. When the user
 * types a query, match quality dominates; when the input is empty, usage
 * dominates (the `defaultRank` helper handles the empty case directly).
 */
export function combinedScore(fuzzy: number, usage: number, hasQuery: boolean): number {
  if (!hasQuery) return usage;         // empty input → pure usage ranking
  return fuzzy * (1 + 0.5 * usage);    // query present → fuzzy boosted by familiarity
}

/** Rank + truncate. Items scoring 0 are dropped. */
export function rankCommands(
  commands: Command[],
  query: string,
  usage: UsageMap,
  limit: number,
): Command[] {
  const hasQuery = query.length > 0;
  const max = maxUseCount(usage);
  const scored: Array<{ cmd: Command; score: number }> = [];
  for (const cmd of commands) {
    const fuzzy = fuzzyScore(query, cmd.label, cmd.description, cmd.keywords);
    if (hasQuery && fuzzy === 0) continue;
    const u = usageScore(cmd.id, usage, max);
    const score = combinedScore(fuzzy, u, hasQuery);
    if (score > 0) scored.push({ cmd, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.cmd);
}

export function rankSearchItems(
  items: SearchItem[],
  query: string,
  usage: UsageMap,
  limit: number,
): SearchItem[] {
  const hasQuery = query.length > 0;
  const max = maxUseCount(usage);
  const scored: Array<{ item: SearchItem; score: number }> = [];
  for (const item of items) {
    const fuzzy = fuzzyScore(query, item.label, item.subtitle, undefined);
    if (hasQuery && fuzzy === 0) continue;
    const u = usageScore(item.id, usage, max);
    const boost = item.boost ?? 1;
    const score = combinedScore(fuzzy, u, hasQuery) * boost;
    if (score > 0) scored.push({ item, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.item);
}
